import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../App.css";
import { apiPostAuth } from "../../../utils/api";

import storeIcon from "../../../assets/app_store_icon.png";

export default function ConnectedAppleAppPage() {
  const { app_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [appDetails, setAppDetails] = useState(null);
  const [reviewAutomationStatus, setReviewAutomationStatus] = useState(null);
  const [statistics, setStatistics] = useState({
    repliedReviews: { today: 0, yesterday: 0, thisMonth: 0, lastMonth: 0 },
    creditUsage: { today: 0, yesterday: 0, thisMonth: 0, lastMonth: 0 },
  });
  const [googlePlayCredits, setGooglePlayCredits] = useState(0);
  const [isAppPaused, setIsAppPaused] = useState(false);
  const [isAppRemoved, setIsAppRemoved] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    action: null, // "start", "stop", or "remove"
  });
  const [password, setPassword] = useState(""); // for app removal

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiPostAuth("apple_apps/get_app_detail.php", {
          app_id: app_id,
        });
        console.log(response)
        if (response.result === 1) {
          if(response.app_data.app_name.length === 0 || response.app_data.app_support_email === 0){
            const appId = app_id;
            navigate("/dashboard/add-app-store-app/app-detail", {
              state: { appId },
            });
          }else{
            setAppDetails({
              app_name: response.app_data.app_name,
              app_id: response.app_data.apple_app_id,
              support_email: response.app_data.app_support_email,
              developer_prompt: response.app_data.developer_prompt,
            });
            setGooglePlayCredits(response.google_play_credit || 0);
            setIsAppPaused(response.is_app_paused === 1);
            setIsAppRemoved(response.is_app_removed === 1);

            const resultStatistics = response.statistics || {};

            setStatistics({
              repliedReviews: { 
                today: parseInt(resultStatistics.today_reply) || 0,
                yesterday: parseInt(resultStatistics.yesterday_reply) || 0,
                thisMonth: parseInt(resultStatistics.this_month_reply) || 0,
                lastMonth: parseInt(resultStatistics.last_month_reply) || 0
              },
              creditUsage: { 
                today: parseInt(resultStatistics.today_credit) || 0,
                yesterday: parseInt(resultStatistics.yesterday_credit) || 0,
                thisMonth: parseInt(resultStatistics.this_month_credit) || 0,
                lastMonth: parseInt(resultStatistics.last_month_credit) || 0
              },
            });


            setReviewAutomationStatus({
              status: response.running_status.is_running === 0 ? "Not Running" : "Running",
              message:
                response.running_status.is_running === 0
                  ? "Auto Reply not Running"
                  : "Auto Reply Running",
            });
          }
          
        } else {
          navigate(`/dashboard/app-store-review`);
        }
      } catch (error) {
        navigate(`/dashboard/app-store-review`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [app_id, navigate]);

  const handleGetCredits = () => {
    navigate("/dashboard/billing-payment");
  };

  const confirmToggleAutomation = () => {
    const isCurrentlyRunning = reviewAutomationStatus?.status === "Running";
    setConfirmModal({
      show: true,
      action: isCurrentlyRunning ? "stop" : "start",
    });
  };

  const toggleAutomation = async () => {
    setConfirmModal({ show: false, action: null });
    const isCurrentlyRunning = reviewAutomationStatus.status === "Running";
    const endpoint = isCurrentlyRunning
      ? "apple_apps/stop_automation.php"
      : "apple_apps/start_automation.php";

    try {
      const response = await apiPostAuth(endpoint, {
        app_id: app_id,
      });
      if (response.result === 1) {
        const newStatus = isCurrentlyRunning ? "Not Running" : "Running";
        setReviewAutomationStatus({
          status: newStatus,
          message: newStatus === "Running" ? "Automation is now active" : "Automation stopped",
        });
        alert(
          newStatus === "Running"
            ? "Automation has been started."
            : "Automation has been stopped."
        );
      } else {
        alert(response.message || "Failed to update automation status.");
      }
    } catch (error) {
      alert("An error occurred while updating the automation status.");
    }
  };

  const confirmRemoveApp = () => {
    setPassword("");
    setConfirmModal({
      show: true,
      action: "remove",
    });
  };

  const handleRemoveApp = async () => {
    if (!password.trim()) {
      alert("Please enter your account password.");
      return;
    }

    try {
      const response = await apiPostAuth("apple_apps/remove_app.php", {
        app_id: app_id,
        password: password,
      });
      if (response.result === 1) {
        alert("The app is scheduled for removal. Redirecting...");
        navigate("/dashboard/app-store-review");
      } else {
        alert(response.message || "Failed to remove app.");
      }
    } catch (error) {
      alert("An error occurred while removing the app.");
    } finally {
      setConfirmModal({ show: false, action: null });
    }
  };

  const simulateReviewResponse = () => {
    navigate(`/dashboard/app-store-review/${app_id}/simulation`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Loading app details...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate(`/dashboard/app-store-review`)}
        style={backButtonStyle}
      >
        ← Back to App List
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <img
          src={storeIcon}
          alt="appstore icon"
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "6px",
            objectFit: "cover"
          }}
        />

        <h1 style={{ fontSize: "1.8rem", color: "#1f2937", margin: 0 }}>
          {appDetails?.app_name || "App"} Dashboard
        </h1>
      </div>

      {/* Warning for paused app */}
      {isAppPaused && (
        <div style={warningBoxStyle}>
          <h3 style={{ color: "#b91c1c", marginBottom: "5px" }}>Automation Paused</h3>
          <p style={{ marginBottom: "10px" }}>
            The automation is paused because your available credits are too low. 10 Credits are required to run the Automation.
          </p>
          <button onClick={handleGetCredits} className="primary-button">
            Get More Credits
          </button>
        </div>
      )}

      {/* Warning for removed app */}
      {isAppRemoved && (
        <div style={infoBoxStyle}>
          <h3 style={{ color: "#92400e", marginBottom: "5px" }}>App Removal in Progress</h3>
          <p>
            You have removed this app. We are processing the app removal, which may take
            up to 1 hour. All actions are currently disabled.
          </p>
        </div>
      )}

      {/* Credits */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Available Credits</h2>
        <p style={{ fontSize: "1.2rem", margin: "10px 0" }}>{googlePlayCredits}</p>
        <button onClick={handleGetCredits} className="primary-button">
          Get Credits
        </button>
      </section>

      {/* App Details */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>App Details</h2>
        <p><strong>App Name:</strong> {appDetails?.app_name}</p>
        <p><strong>App ID:</strong> {appDetails?.app_id}</p>
        <p><strong>App Support Email:</strong> {appDetails?.support_email}</p>
        <p><strong>Developer Prompt:</strong> {appDetails?.developer_prompt || " - "}</p>
        <button
          onClick={() => navigate(`/dashboard/app-store-review/${app_id}/edit`)}
          className="secondary-button"
          style={{ marginTop: "10px" }}
          disabled={isAppRemoved}
        >
          Edit Details
        </button>
        <button
          onClick={simulateReviewResponse}
          className="primary-button"
          style={{ marginTop: "10px", marginLeft: "10px" }}
          disabled={isAppRemoved}
        >
          Edit Prompt & Simulate Review Responses
        </button>
      </section>

      {/* Review Automation */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Review Automation Status</h2>
        <p><strong>Status:</strong> {reviewAutomationStatus?.status}</p>
        <p><strong>Message:</strong> {reviewAutomationStatus?.message}</p>
        <button
          onClick={confirmToggleAutomation}
          className="primary-button"
          style={{ marginTop: "10px" }}
          disabled={isAppRemoved}
        >
          {reviewAutomationStatus?.status === "Running" ? "Stop Automation" : "Run Automation"}
        </button>
      </section>

      {/* Statistics */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Statistics</h2>

        <h3 style={statsTitle}>Replied Reviews</h3>
        <div style={statsCardContainer}>
          <div style={statsCard}>
            <p style={statsCardLabel}>Today</p>
            <p style={statsCardValue}>{statistics.repliedReviews.today}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>Yesterday</p>
            <p style={statsCardValue}>{statistics.repliedReviews.yesterday}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>This Month</p>
            <p style={statsCardValue}>{statistics.repliedReviews.thisMonth}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>Last Month</p>
            <p style={statsCardValue}>{statistics.repliedReviews.lastMonth}</p>
          </div>
        </div>

        <h3 style={{ ...statsTitle, marginTop: "20px" }}>Credit Usage</h3>
        <div style={statsCardContainer}>
          <div style={statsCard}>
            <p style={statsCardLabel}>Today</p>
            <p style={statsCardValue}>{statistics.creditUsage.today}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>Yesterday</p>
            <p style={statsCardValue}>{statistics.creditUsage.yesterday}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>This Month</p>
            <p style={statsCardValue}>{statistics.creditUsage.thisMonth}</p>
          </div>
          <div style={statsCard}>
            <p style={statsCardLabel}>Last Month</p>
            <p style={statsCardValue}>{statistics.creditUsage.lastMonth}</p>
          </div>
        </div>
      </section>

      {/* Remove App */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Remove App</h2>
        <p style={{ fontSize: "0.9rem", color: "#6b7280" }}>
          Warning: This action is not reversible. The app automation will stop immediately,
          and the system will remove all the app data within 1 hour.
        </p>
        <button
          onClick={confirmRemoveApp}
          className="secondary-button"
          style={{ marginTop: "10px" }}
          disabled={isAppRemoved}
        >
          Remove App
        </button>
      </section>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3>
              {confirmModal.action === "remove"
                ? "Confirm App Removal"
                : confirmModal.action === "start"
                ? "Start Review Automation?"
                : "Stop Review Automation?"}
            </h3>
            {confirmModal.action === "remove" && (
              <>
                <p>
                  Please enter your account password to confirm the app removal.
                </p>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyle}
                />
              </>
            )}
            <p style={{ marginTop: "10px" }}>
              Are you sure you want to {confirmModal.action} this app?
            </p>
            <div style={{ marginTop: "20px" }}>
              <button
                className="primary-button"
                onClick={
                  confirmModal.action === "remove"
                    ? handleRemoveApp
                    : toggleAutomation
                }
                style={{ marginRight: "10px", width: "100px" }}
              >
                Yes
              </button>
              <button
                className="secondary-button"
                onClick={() => setConfirmModal({ show: false, action: null })}
                style={{ width: "100px" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Styles --- */
const sectionStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "1.2rem",
  marginBottom: "10px",
  color: "#1f2937",
};

const warningBoxStyle = {
  backgroundColor: "#fff3f3",
  padding: "15px",
  border: "1px solid #fca5a5",
  borderRadius: "6px",
  marginBottom: "20px",
};

const infoBoxStyle = {
  backgroundColor: "#fef3c7",
  padding: "15px",
  border: "1px solid #fcd34d",
  borderRadius: "6px",
  marginBottom: "20px",
};

const statsCardContainer = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: "15px",
  marginTop: "10px",
};

const statsCard = {
  backgroundColor: "#f9fafb",
  padding: "15px",
  borderRadius: "8px",
  textAlign: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
};

const statsCardLabel = {
  fontSize: "0.9rem",
  color: "#6b7280",
  marginBottom: "6px",
};

const statsCardValue = {
  fontSize: "1.4rem",
  fontWeight: "700",
  color: "#2563eb",
};

const statsTitle = {
  fontSize: "1.1rem",
  fontWeight: "600",
  color: "#374151",
  marginBottom: "10px",
};

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
  transition: "all 0.2s ease",
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
  textAlign: "center",
  width: "300px",
};

const inputStyle = {
  width: "90%",
  padding: "8px",
  marginTop: "10px",
  border: "1px solid #ccc",
  borderRadius: "6px",
};
